import {
    LocalAttachment,
    MessageComposer,
    MessageCompositionMiddleware,
} from "stream-chat";

const ATTACHMENTS_MIDDLEWARE_ID =
    "stream-io/message-composer-middleware/attachments";

function stripLocalMetadata({
    localMetadata: _localMetadata,
    ...attachment
}: LocalAttachment) {
    return attachment;
}

/**
 * Stream's default attachments composition middleware only keeps file uploads
 * (attachments whose localMetadata.uploadState is "finished"). Custom attachments
 * like "exercise" and "goals_board" are sent by the backend and never go through
 * the upload flow, so when a message is edited they are silently dropped on submit.
 *
 * This replaces the default middleware with one that additionally preserves any
 * attachment that is not a file upload, keeping custom attachments intact on edit.
 */
export function preserveCustomAttachmentsOnEdit({
    composer,
}: {
    composer: MessageComposer;
}): void {
    const middleware: MessageCompositionMiddleware = {
        id: ATTACHMENTS_MIDDLEWARE_ID,
        handlers: {
            compose: ({ state, next, discard, forward }) => {
                const { attachmentManager } = composer;
                if (!attachmentManager) {
                    return forward();
                }

                if (attachmentManager.uploadsInProgressCount > 0) {
                    composer.client.notifications.addWarning({
                        message: "Wait until all attachments have uploaded",
                        origin: {
                            emitter: "MessageComposer",
                            context: { composer },
                        },
                        options: {
                            type: "validation:attachment:upload:in-progress",
                        },
                    });
                    return discard();
                }

                const customAttachments = attachmentManager.attachments.filter(
                    (attachment) => !attachment.localMetadata.uploadState,
                );

                const attachments = (state.message.attachments ?? [])
                    .concat(
                        attachmentManager.successfulUploads.map(
                            stripLocalMetadata,
                        ),
                    )
                    .concat(customAttachments.map(stripLocalMetadata));

                if (!attachments.length) {
                    return forward();
                }

                return next({
                    ...state,
                    localMessage: {
                        ...state.localMessage,
                        attachments,
                    },
                    message: {
                        ...state.message,
                        attachments,
                    },
                });
            },
        },
    };

    composer.compositionMiddlewareExecutor.replace([middleware]);
}
